import { api } from "./axiosInstance";
import axios, { type AxiosRequestConfig } from "axios";
import { API_URL } from "../config";
import { RefreshWaitingQueue } from "../utils/RefreshWaitingQueue";
import { clearAppSession } from "../features/common/redux/globalActions";
import { sleep } from "../utils";
import { authActions } from "../features/auth/redux/slice";

const refreshQueue = new RefreshWaitingQueue();

type NotifyFn = (
  type: "success" | "error" | "info" | "warning",
  message: string,
  description?: string,
) => void;

const setup = (store: any, notify: NotifyFn) => {
  api.interceptors.request.use(
    (config) => {
      const token = store.getState().auth.access;
      if (token) {
        config.headers["Authorization"] = "Bearer " + token;
      }
      return config;
    },
    async (error) => {
      return Promise.reject(error);
    },
  );

  api.interceptors.response.use(
    (res) => {
      return res;
    },
    async (error) => {
      const originalRequest = error.config as AxiosRequestConfig & {
        _retry: undefined | boolean;
        _notified?: boolean;
      };

      // 🌐 NETWORK ERROR (no response from server)
      if (!error.response && !originalRequest._notified) {
        notify(
          "error",
          "Network error",
          "Unable to connect to the server. Please check your internet connection and try again.",
        );
        originalRequest._notified = true;
        return Promise.reject(error);
      }

      if (!error.response || !error.response.status) {
        return Promise.reject(error);
      }

      const accessToken = !!store.getState().auth.access;
      if (
        accessToken &&
        error.response.status === 401 &&
        !originalRequest._retry
      ) {
        if (refreshQueue.isRefreshing) {
          return await refreshQueue.push(originalRequest);
        }
        refreshQueue.isRefreshing = true;
        originalRequest._retry = true;

        try {
          const { data } = await axios.post(
            API_URL + "/api/v1/auth/refresh-token/",
            {
              refresh: store.getState().auth.refresh,
            },
          );

          store.dispatch(authActions.setAuthToken(data));
          api.defaults.headers.common["Authorization"] =
            "Bearer " + data.access;

          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: "Bearer " + data.access,
          };
          refreshQueue.isRefreshing = false;
          refreshQueue.resolveQueued(data.access);
          return await api(originalRequest);
        } catch (error: any) {
          if (
            error?.response?.status === 401 ||
            error?.response?.status === 406
          ) {
            refreshQueue.rejectQueued(error);
            refreshQueue.isRefreshing = false;
            await sleep(500);
            store.dispatch(clearAppSession());
          }
        }
      } else if (
        accessToken &&
        (error.response.status === 401 || error.response.status === 406)
      ) {
        refreshQueue.rejectQueued(error);
        refreshQueue.isRefreshing = false;
        store.dispatch(clearAppSession());
      }

      return Promise.reject(error);
    },
  );
};

export default setup;
